'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Mail } from 'lucide-react';

const InstagramIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
  </svg>
);

const StravaIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
  </svg>
);

const Dot = () => (
  <span style={{ color: '#D8D8CE', userSelect: 'none' }}>·</span>
);

const linkBase: React.CSSProperties = {
  fontSize: 12,
  color: '#5F625F',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  transition: 'color 0.12s ease',
  fontFamily: 'var(--font-body, sans-serif)',
};

const legalLinkBase: React.CSSProperties = {
  fontSize: 11,
  color: '#8A8D8A',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
  transition: 'color 0.12s ease',
  fontFamily: 'var(--font-mono, monospace)',
  letterSpacing: '0.02em',
};

function NavLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      style={linkBase}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#070707'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#5F625F'; }}
    >
      {children}
    </Link>
  );
}

function LegalLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link
      href={href}
      style={legalLinkBase}
      onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#5F625F'; }}
      onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#8A8D8A'; }}
    >
      {children}
    </Link>
  );
}

export default function Footer() {
  const { t } = useLanguage();
  const { user, isAdmin, loading: authLoading } = useAuth();

  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => { setMounted(true); }, []);

  const isLoggedIn = mounted && !authLoading && !!user;
  const showAdmin = mounted && !authLoading && isAdmin;

  return (
    <footer style={{ borderTop: '1px solid #D8D8CE', backgroundColor: '#F7F7F2', marginTop: 64 }}>
      <div style={{ maxWidth: 1536, margin: '0 auto', padding: '0 24px' }}>

        {/* Main compact row */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px 24px',
          padding: '14px 0',
          borderBottom: '1px solid #EBEBEB',
        }}>
          {/* Left: wordmark + nav links */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 16px' }}>
            <span style={{
              fontFamily: 'var(--font-heading, "Arial Black", sans-serif)',
              fontWeight: 900,
              fontSize: 13,
              color: '#070707',
              textTransform: 'uppercase',
              letterSpacing: '-0.01em',
              marginRight: 8,
            }}>
              R3SET
            </span>

            <NavLink href="/">{t('Home', '首页')}</NavLink>
            <Dot />
            <NavLink href="/weekly-plan">{t('Weekly Plan', '训练计划')}</NavLink>
            <Dot />
            <NavLink href="/events">{t('Events', '活动')}</NavLink>
            <Dot />
            <NavLink href="/community">{t('Community', '社群')}</NavLink>
          </div>

          {/* Right: member + social */}
          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px 16px' }}>
            {!isLoggedIn ? (
              <>
                <NavLink href="/sign-up-login">{t('Login', '登录')}</NavLink>
                <Dot />
                <NavLink href="/apply">{t('Apply', '申请')}</NavLink>
              </>
            ) : (
              <>
                <NavLink href="/my-dashboard">{t('Dashboard', '仪表板')}</NavLink>
                <Dot />
                <NavLink href="/my-calendar">{t('Calendar', '日历')}</NavLink>
                {showAdmin && (
                  <>
                    <Dot />
                    <NavLink href="/admin-dashboard">{t('Admin', '管理')}</NavLink>
                  </>
                )}
              </>
            )}

            <span style={{ width: 1, height: 14, backgroundColor: '#D8D8CE', display: 'inline-block', margin: '0 4px' }} />

            {/* Social links — horizontal: Instagram · Strava · Email */}
            <a
              href="https://www.instagram.com/r3setrun/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...linkBase, display: 'flex', alignItems: 'center', gap: 5 }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#070707'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#5F625F'; }}
            >
              <InstagramIcon />
              Instagram
            </a>
            <Dot />
            <a
              href="https://strava.app.link/YFkQtPNaY3b"
              target="_blank"
              rel="noopener noreferrer"
              style={{ ...linkBase, display: 'flex', alignItems: 'center', gap: 5 }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#070707'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#5F625F'; }}
            >
              <StravaIcon />
              Strava
            </a>
            <Dot />
            <a
              href="mailto:hello@r3set.run"
              style={{ ...linkBase, display: 'flex', alignItems: 'center', gap: 5 }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = '#070707'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = '#5F625F'; }}
            >
              <Mail size={12} />
              {t('Email', '邮件')}
            </a>
          </div>
        </div>

        {/* Bottom micro row: legal + copyright */}
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '6px 16px',
          padding: '8px 0',
        }}>
          <p style={{ fontSize: 11, color: '#8A8D8A', fontFamily: 'var(--font-mono, monospace)', letterSpacing: '0.03em', margin: 0 }}>
            © 2026 R3SET Running
          </p>

          <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 12px' }}>
            <LegalLink href="/privacy-policy">{t('Privacy', '隐私')}</LegalLink>
            <Dot />
            <LegalLink href="/terms-of-use">{t('Terms', '条款')}</LegalLink>
            <Dot />
            <LegalLink href="/cookie-policy">{t('Cookies', 'Cookie')}</LegalLink>
            <Dot />
            <LegalLink href="/contact">{t('Contact', '联系')}</LegalLink>
          </div>
        </div>

      </div>
    </footer>
  );
}